/**
 * Compliance status utilities
 *
 * Centralized status labels, colors, and conversion functions for consistent UI display.
 * Single source of truth: ControlEvidenceStatusEnum from database.
 */

import type { ControlEvidenceStatusEnum, ControlTreeStatus, ControlTreeNode, TaskDTO, ApiCheckHealth, TestEvidencePreparation } from '@deepguide-ai/types';

// ===== Status Labels =====

export interface StatusLabelConfig {
  label: string;
  color: 'green' | 'red' | 'amber' | 'orange' | 'blue' | 'muted';
}

export const STATUS_PILL_CLASS: Record<StatusLabelConfig['color'], string> = {
  green:
    'bg-status-green-bg text-status-green-strong border-status-green/30',
  red:
    'bg-status-red-bg text-status-red-strong border-status-red/30',
  amber:
    'bg-status-orange-bg text-status-orange-strong border-status-orange/30',
  orange:
    'bg-orange-50 text-orange-700 border-orange-200 dark:border-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  blue:
    'bg-primary-subtle text-status-blue-strong border-cobalt-border dark:border-cobalt-strong/30 dark:text-cobalt-strong',
  muted:
    'bg-[#F4F4F2] text-muted-foreground border-border dark:bg-muted',
};

export const STATUS_LABELS: Record<string, StatusLabelConfig> = {
  // ControlEvidenceStatusEnum (single source of truth from database)
  NOT_STARTED: { label: 'Needs work', color: 'muted' },
  IN_PROGRESS: { label: 'Needs work', color: 'blue' },
  // PASSED is a *pre-approval* state: evidence was submitted and cleared the
  // automated/AI quality check, but a human reviewer still has to Approve it.
  // On the compliance axis it is the same "in review" state as SUBMITTED — the
  // pass verdict lives on the parallel axis (API health badge / quality score),
  // so both render "Awaiting review" and only APPROVED reads as done.
  PASSED: { label: 'In review', color: 'amber' },
  FAILED: { label: 'Needs work', color: 'red' },
  INCONCLUSIVE: { label: 'Needs work', color: 'amber' },
  SUBMITTED: { label: 'In review', color: 'amber' },
  NEEDS_RETEST: { label: 'Needs work', color: 'orange' },
  APPROVED: { label: 'Approved', color: 'green' },
  NOT_APPLICABLE: { label: 'N/A', color: 'muted' },
  NON_PERFORMING: { label: 'Needs work', color: 'muted' },
  ON_HOLD: { label: 'Needs work', color: 'muted' },
  BLOCKED: { label: 'Needs work', color: 'orange' },
  // EvidenceReviewStatus (for submission workflow display)
  PENDING: { label: 'In review', color: 'amber' },
  WITHDRAWN: { label: 'Withdrawn', color: 'muted' },
};

export const CONTROL_APPROVAL_STATUS = {
  LEGACY_ACCEPTED: { label: 'Approved', status: 'APPROVED' },
  PREPARE: { label: 'Needs work', status: 'NOT_STARTED' },
  NEEDS_APPROVAL: { label: 'In review', status: 'SUBMITTED' },
  APPROVED: { label: 'Approved', status: 'APPROVED' },
  CHANGES_REQUESTED: { label: 'Needs work', status: 'NEEDS_RETEST' },
  NOT_APPLICABLE: { label: 'N/A', status: 'NOT_APPLICABLE' },
} as const;

/**
 * Map ControlEvidenceStatusEnum to the unified task-board column status vocabulary
 * (OPEN/ASSIGNED/IN_PROGRESS/BLOCKED/IN_REVIEW/RESOLVED/DISMISSED).
 */
export const CES_STATUS_TO_TASK_STATUS: Record<ControlEvidenceStatusEnum, string> = {
  ON_HOLD: 'OPEN',
  NOT_STARTED: 'ASSIGNED',
  NEEDS_RETEST: 'ASSIGNED',
  FAILED: 'ASSIGNED',
  IN_PROGRESS: 'IN_PROGRESS',
  BLOCKED: 'BLOCKED',
  SUBMITTED: 'IN_REVIEW',
  PASSED: 'IN_REVIEW',
  APPROVED: 'RESOLVED',
  NOT_APPLICABLE: 'DISMISSED',
  NON_PERFORMING: 'DISMISSED',
};

/**
 * The status a Test should be *rendered* with.
 *
 * A Test keeps its durable execution status, but requirement scope can make it
 * effectively N/A without touching it (server: effective-test-status.service).
 * Every surface must ask this rather than reading the raw field directly —
 * doing that is what made the detail page read "To do" for a Test the list
 * showed as N/A. Mutations still target the raw status; this is display only.
 *
 * The caller names its own raw field because the API returns a Test's CES
 * status under different names — `status` on the tests list, `evidenceStatus`
 * on requirement-tree nodes, where `status` is the unrelated rollup vocabulary
 * (PASSED/FAILED/…) and must NOT be used here.
 */
export function displayStatusOf(test: {
  effectiveStatus?: string | null
  status?: string | null
}): string | undefined {
  return test.effectiveStatus ?? test.status ?? undefined
}

/**
 * Terminal CES statuses — mirrors the server's `TERMINAL_CES_STATUSES`
 * (services/tests/control-evidence-status.service.ts). Once a test reaches one
 * of these, an outstanding
 * Vera escalation is stale: every projection (inbox chip, priority buckets,
 * Blocked lane) already treats it as inactive, so UI surfaces that key off
 * `agentEscalationReason` must gate on this too.
 */
export const TERMINAL_CES_STATUSES: ReadonlySet<ControlEvidenceStatusEnum> = new Set([
  'APPROVED',
  'NOT_APPLICABLE',
  'NON_PERFORMING',
]);

/** True when the test has reached a terminal status (see TERMINAL_CES_STATUSES). */
export function isTerminalCesStatus(status: string | null | undefined): boolean {
  return !!status && TERMINAL_CES_STATUSES.has(status as ControlEvidenceStatusEnum);
}

/**
 * Get the display label for a status
 */
export function getStatusLabel(status: string | null | undefined, preparation?: TestEvidencePreparation): string {
  if (preparation && status !== 'NOT_APPLICABLE') return { NEEDS_WORK: 'Needs work', SUBMITTED: 'Submitted', MONITORING: 'Monitoring', LEGACY_ACCEPTED: 'Submitted' }[preparation];
  if (!status) return 'Unknown';
  const config = STATUS_LABELS[status as keyof typeof STATUS_LABELS];
  return config?.label ?? status;
}

/**
 * Get the color for a status
 */
export function getStatusColor(status: string | null | undefined): StatusLabelConfig['color'] {
  if (!status) return 'muted';
  const config = STATUS_LABELS[status as keyof typeof STATUS_LABELS];
  return config?.color ?? 'muted';
}

export function getStatusBadgeColor(
  status: string | null | undefined
): 'green' | 'red' | 'amber' | 'blue' | 'default' {
  const color = getStatusColor(status)
  if (color === 'orange') return 'amber'
  if (color === 'muted') return 'default'
  return color
}

/**
 * Project an API test's latest run into the parallel "check health" signal
 * (RFC-189). Client mirror of the server's deriveLatestApiCheckHealth — used on
 * the test detail page, where the row already has the full ApiTestRunDTO. A
 * conclusive verdict wins; a queued/in-flight run reads as RUNNING; a finished
 * run with no verdict returns null (no badge).
 */
export function deriveApiCheckHealth(
  run: { resultStatus?: string | null; phase?: string | null } | null | undefined,
): ApiCheckHealth | null {
  if (!run) return null;
  if (run.resultStatus) return run.resultStatus as ApiCheckHealth;
  if (run.phase === 'QUEUED' || run.phase === 'RUNNING') return 'RUNNING';
  return null;
}

// ===== ControlTreeNode to TaskDTO Conversion =====

/**
 * Map ControlTreeStatus to ControlEvidenceStatusEnum
 * Used as fallback when evidenceStatus is not available (backward compatibility)
 */
const TREE_STATUS_TO_EVIDENCE_STATUS: Record<ControlTreeStatus, ControlEvidenceStatusEnum> = {
  PASSED: 'PASSED',
  FAILED: 'FAILED',
  NEEDS_REVIEW: 'SUBMITTED',
  NOT_TESTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  PARTIAL: 'IN_PROGRESS',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
};

/**
 * Convert ControlTreeNode (test type) to TaskDTO for StatusRow display
 */
export function controlTreeNodeToTaskDTO(
  node: ControlTreeNode,
  controlId?: string,
  currentFrameworkSlug?: string,
): TaskDTO {
  // Build displayId from controlId and taskNumber
  const displayId =
    node.taskCode ||
    (controlId
      ? node.taskNumber
        ? `${controlId}-${String(node.taskNumber).padStart(2, '0')}`
        : controlId
      : node.id);

  return {
    statusId: node.statusId || node.id,
    assignedTo: node.assignee?.id ?? null,
    dueDate: node.dueDate ?? null,
    externalTicketUrl: null,
    lastTestedAt: node.lastTestedAt ?? null,
    templateId: node.templateId ?? null,
    templateType: node.templateType ?? null,
    evidenceCategory: node.evidenceCategory ?? null,
    topicId: node.topicId ?? null,
    displayId,
    title: node.name,
    isAdHoc: node.isAdHoc ?? false,
    controls: controlId ? [{
      id: controlId,
      controlId: controlId,
      title: '',
      frameworkId: '',
      frameworkName: null,
    }] : [],
    // Requirement pages show only the selected framework's citations. General
    // Test surfaces omit currentFrameworkSlug and retain the full cross-framework
    // citation set required by RFC-157.
    frameworkCitations: currentFrameworkSlug
      ? node.slotCitations?.filter(
          (citation) => citation.frameworkSlug === currentFrameworkSlug,
        )
      : node.slotCitations,
    // Use evidenceStatus (raw database status) if available, fall back to mapping for backward compatibility
    status: node.evidenceStatus ?? TREE_STATUS_TO_EVIDENCE_STATUS[node.status],
    // #1704: the server derives effective N/A from requirement scope onto every
    // tree node; dropping it here is how a test whose only in-scope citations
    // are N/A requirements rendered as "To Do" on the framework page while the
    // /tests list showed it as N/A. `displayStatusOf` prefers this when set.
    effectiveStatus: node.effectiveStatus,
    // API check health — parallel to (not merged into) the submission status above
    latestApiRunStatus: node.latestApiRunStatus ?? null,
    submission: null,
    assignee: node.assignee
      ? {
          id: node.assignee.id,
          name: node.assignee.name ?? '',
          image: node.assignee.image,
        }
      : null,
    // RFC-107: Subtask stats for sampling tests
    subtaskStats: node.subtaskStats ?? null,
    hasSample: node.hasSample ?? null,
    // Labels
    labels: node.labels as TaskDTO['labels'],
    // RFC-234 — carry the derived inclusion view so a row rendered from the
    // requirement tree shows the same chips as the same row in the Tests list.
    // Both are optional on the node, so a caller that never asked for them is
    // unchanged.
    inclusion: node.inclusion,
    scopeFactLabel: node.scopeFactLabel,
    collection: node.collection,
    workflowVersion: node.workflowVersion,
    preparation: node.preparation,
  };
}

/**
 * Flatten the Tests owned by an already-filtered Control tree. The Control
 * query remains authoritative for which Controls belong to the View; this
 * helper only changes their presentation and removes duplicates caused by a
 * Test being linked through more than one matching Control/category.
 */
export function controlTreeToUniqueTasks(
  nodes: ControlTreeNode[],
  currentFrameworkSlug?: string,
): TaskDTO[] {
  const tasks = new Map<string, TaskDTO>();

  const visit = (items: ControlTreeNode[], parentControlId?: string) => {
    for (const item of items) {
      const controlId = item.type === 'control' ? item.controlId : parentControlId;
      if (item.type === 'test' || item.type === 'anchor') {
        const task = controlTreeNodeToTaskDTO(
          item,
          controlId,
          currentFrameworkSlug,
        );
        if (!tasks.has(task.statusId)) tasks.set(task.statusId, task);
      }
      if (item.children?.length) visit(item.children, controlId);
    }
  };

  visit(nodes);
  return [...tasks.values()];
}
